// useCurrentEmployee — the employee context + accessor hook.
//
// Split out of `EmployeeContext.tsx` so that module only exports components
// (react-refresh/only-export-components).

import { createContext, useContext } from "react";

import type { Employee } from "@shared/types/employee";
import type { EmployeeRole } from "@shared/types";

// Scopes required for Google API features (Drive, Docs, Gmail, Sheets)
export const REQUIRED_GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/drive',
  'https://www.googleapis.com/auth/documents',
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/spreadsheets.readonly',
];

export interface EmployeeContextValue {
  employee: Employee | null;
  roles: EmployeeRole[];
  isLoading: boolean;
  hasRole: (...roles: EmployeeRole[]) => boolean;
  googleScopesOk: boolean | null; // null = still checking
  recheckGoogleScopes: () => Promise<void>;
}

export const EmployeeContext = createContext<EmployeeContextValue>({
  employee: null,
  roles: [],
  isLoading: true,
  hasRole: () => false,
  googleScopesOk: null,
  recheckGoogleScopes: async () => {},
});

export const useCurrentEmployee = () => useContext(EmployeeContext);
